import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CaseCptLine } from '@/lib/templates/types';
import { logCptEdit } from '@/lib/audit/logCptEdit';

interface ApplyToTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line: CaseCptLine | null;
  templateId?: string;
  templateName?: string;
  onTemplateUpdated?: () => void;
  /** 'procedure' or 'modifier' */
  changeType: 'procedure' | 'modifier';
  /** For modifier changes */
  modifierCode?: string;
  caseId?: string;
}

export function ApplyToTemplateDialog({
  open,
  onOpenChange,
  line,
  templateId,
  templateName,
  onTemplateUpdated,
  changeType,
  modifierCode,
  caseId,
}: ApplyToTemplateDialogProps) {
  const [confirming, setConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  if (!line) return null;

  const label = changeType === 'modifier'
    ? `Modifier ${modifierCode} on ${line.code}`
    : `${line.code} — ${line.description}`;

  const handleApplyToTemplate = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }

    if (!templateId) {
      toast.error('No template linked to this case');
      return;
    }

    setIsSaving(true);
    try {
      const { data: template, error: fetchErr } = await supabase
        .from('surgeon_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchErr || !template) throw new Error('Failed to load template');

      const currentVersion = template.version ?? 1;
      const newVersion = currentVersion + 1;

      let newPrimary = Array.isArray(template.primary_cpts) ? [...template.primary_cpts] : [];
      let newSecondary = Array.isArray(template.secondary_cpts) ? [...template.secondary_cpts] : [];
      let changeDesc = '';

      if (changeType === 'procedure') {
        const entry = { code: line.code, description: line.description, default_modifiers: line.modifiers };
        if (line.section === 'primary') {
          if (!newPrimary.some((c: any) => c.code === line.code)) {
            newPrimary.push(entry);
          }
          changeDesc = `Added ${line.code} to primary procedures`;
        } else {
          if (!newSecondary.some((c: any) => c.code === line.code)) {
            newSecondary.push(entry);
          }
          changeDesc = `Added ${line.code} to secondary procedures`;
        }
      } else if (changeType === 'modifier' && modifierCode) {
        const updateList = (list: any[]) =>
          list.map((c: any) => {
            if (c.code === line.code) {
              const mods = Array.isArray(c.default_modifiers) ? [...c.default_modifiers] : [];
              if (!mods.includes(modifierCode)) mods.push(modifierCode);
              return { ...c, default_modifiers: mods };
            }
            return c;
          });
        newPrimary = updateList(newPrimary);
        newSecondary = updateList(newSecondary);
        changeDesc = `Added modifier ${modifierCode} to ${line.code}`;
      }

      await supabase.from('surgeon_template_versions').insert({
        template_id: templateId,
        version: newVersion,
        primary_cpts: newPrimary,
        secondary_cpts: newSecondary,
        narrative_template: template.narrative_template,
        common_addons: template.common_addons,
        change_description: changeDesc,
        changed_by: 'surgeon',
      });

      const { error: updateErr } = await supabase
        .from('surgeon_templates')
        .update({
          primary_cpts: newPrimary,
          secondary_cpts: newSecondary,
          version: newVersion,
          updated_at: new Date().toISOString(),
        })
        .eq('id', templateId);

      if (updateErr) throw updateErr;

      onTemplateUpdated?.();

      // Audit log
      if (caseId) {
        logCptEdit({
          caseId,
          action: changeType === 'modifier' ? 'add_modifier' : 'add_procedure',
          cptCode: line.code,
          modifierCode: changeType === 'modifier' ? modifierCode : undefined,
          section: line.section,
          scope: 'master_template',
          templateId,
          templateVersionBefore: currentVersion,
          templateVersionAfter: newVersion,
          details: { description: line.description, changeDesc } as any,
        });
      }

      toast.success(`Template updated to v${newVersion}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update template');
    } finally {
      setIsSaving(false);
      setConfirming(false);
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    setConfirming(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Apply to Master Template?</DialogTitle>
          <DialogDescription>
            {label}
          </DialogDescription>
        </DialogHeader>

        {confirming ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-destructive">Permanent Template Change</p>
                <p className="text-muted-foreground mt-1">
                  This will update "{templateName}" and create a new version. All future cases will include this change.
                </p>
              </div>
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button onClick={handleApplyToTemplate} disabled={isSaving} className="w-full">
                {isSaving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Confirm — Update Template
              </Button>
              <Button variant="ghost" onClick={handleClose} className="w-full">Cancel</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Button variant="outline" onClick={() => {
              if (caseId && line) {
                logCptEdit({
                  caseId,
                  action: changeType === 'modifier' ? 'add_modifier' : 'add_procedure',
                  cptCode: line.code,
                  modifierCode: changeType === 'modifier' ? modifierCode : undefined,
                  section: line.section,
                  scope: 'case_only',
                  templateId,
                  details: { description: line.description } as any,
                });
              }
              handleClose();
            }} className="w-full">
              This case only
            </Button>
            <Button
              onClick={handleApplyToTemplate}
              disabled={!templateId}
              className="w-full"
            >
              Apply to master template ({templateName || 'Template'})
            </Button>
            <Button variant="ghost" onClick={handleClose} className="w-full">Cancel</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
